import { createInsightsCache } from './insightsCache.js';

// FastAPI backend adapter.
// Set VITE_API_BASE in .env (default: /api).
//
// Insights and media are single-language records (each row has a `lang` of
// 'cn' or 'en'). Public reads pass the current language; admin reads omit it
// to see every record.

const BASE = import.meta.env.VITE_API_BASE || '/api';

function token() {
  return localStorage.getItem('token') || '';
}

async function request(path, opts = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const tok = token();
  if (tok) headers['Authorization'] = `Bearer ${tok}`;

  const res = await fetch(`${BASE}${path}`, { headers, ...opts });
  if (!res.ok) {
    // An expired/invalid token on an authed call returns 401. Clear it and send
    // the admin to the login page instead of surfacing a raw error. Exclude the
    // login call itself (it 401s on bad credentials, handled inline by Login.jsx)
    // and guard against a redirect loop when already on the login page.
    if (res.status === 401 && path !== '/auth/login') {
      localStorage.removeItem('token');
      if (typeof window !== 'undefined' && window.location.pathname !== '/admin/login') {
        window.location.assign('/admin/login');
      }
    }
    const body = await res.text().catch(() => '');
    const error = new Error(`${res.status}${body ? ` — ${body}` : ''}`);
    error.status = res.status;
    const retryAfter = res.headers.get('Retry-After');
    if (retryAfter) {
      error.retryAfterMs = /^\d+$/.test(retryAfter)
        ? Number(retryAfter) * 1000 : Math.max(0, Date.parse(retryAfter) - Date.now()) || 0;
    }
    throw error;
  }
  return res.json();
}

// Prefix uploaded-file paths with the API base so they resolve in any env.
function resolveUpload(src) {
  if (!src) return '';
  return src.startsWith('/uploads/') ? BASE + src : src;
}

export const api = {
  // --- Auth ---
  login: (username, password) =>
    request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  // --- Insights (public read) ---
  insights: async (opts = {}) => {
    const qs = new URLSearchParams();
    if (opts.lang)          qs.set('lang',  String(opts.lang));
    if (opts.status)        qs.set('status', String(opts.status));
    if (opts.date_from)     qs.set('date_from', String(opts.date_from));
    if (opts.date_to)       qs.set('date_to', String(opts.date_to));
    if (opts.page != null)  qs.set('page',  String(opts.page));
    if (opts.limit != null) qs.set('limit', String(opts.limit));
    const suffix = qs.toString() ? `?${qs}` : '';
    const json = await request(`/insights${suffix}`, { signal: opts.signal });
    if (!Array.isArray(json.data)) throw new TypeError('Invalid article list response');
    return { data: json.data, meta: json.meta || {} };
  },

  insight: async (id) => {
    const json = await request(`/insights/${id}`);
    return json.data || null;
  },

  // --- Insights (admin write) ---
  createInsight: async (data) => {
    const result = await request('/insights', { method: 'POST', body: JSON.stringify(data) });
    publicInsights.invalidate();
    return result;
  },

  updateInsight: async (id, data) => {
    const result = await request(`/insights/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    publicInsights.invalidate();
    return result;
  },

  deleteInsight: async (id) => {
    const result = await request(`/insights/${id}`, { method: 'DELETE' });
    publicInsights.invalidate();
    return result;
  },

  // --- Media (public read) ---
  mediums: async (opts = {}) => {
    const qs = new URLSearchParams();
    if (opts.lang) qs.set('lang', String(opts.lang));
    const suffix = qs.toString() ? `?${qs}` : '';
    const json = await request(`/media${suffix}`, { signal: opts.signal });
    return (json.data || []).map(item => ({
      ...item,
      videoUrl:   resolveUpload(item.video_url),
      coverImage: resolveUpload(item.cover_image),
      autoPlay:   !!item.auto_play,
    }));
  },

  mediaItem: async (id) => {
    const json = await request(`/media/${id}`);
    if (!json.data) return null;
    return {
      ...json.data,
      videoUrl:   resolveUpload(json.data.video_url),
      coverImage: resolveUpload(json.data.cover_image),
      autoPlay:   !!json.data.auto_play,
    };
  },

  // Admin media list: returns { data, meta } and forwards filters + pagination.
  // (Distinct from `mediums`, which returns a bare array for the public carousel.)
  mediaList: async (opts = {}) => {
    const qs = new URLSearchParams();
    if (opts.lang)          qs.set('lang', String(opts.lang));
    if (opts.status)        qs.set('status', String(opts.status));
    if (opts.date_from)     qs.set('date_from', String(opts.date_from));
    if (opts.date_to)       qs.set('date_to', String(opts.date_to));
    if (opts.page != null)  qs.set('page', String(opts.page));
    if (opts.limit != null) qs.set('limit', String(opts.limit));
    const suffix = qs.toString() ? `?${qs}` : '';
    const json = await request(`/media${suffix}`);
    return {
      data: (json.data || []).map(item => ({
        ...item,
        videoUrl:   resolveUpload(item.video_url),
        coverImage: resolveUpload(item.cover_image),
        autoPlay:   !!item.auto_play,
      })),
      meta: json.meta || {},
    };
  },

  // Admin: fetch a single media item UNMAPPED (snake_case fields) for the editor.
  mediaEdit: async (id) => {
    const json = await request(`/media/${id}`);
    return json.data || null;
  },

  // --- Media (admin write) ---
  createMedia: (data) =>
    request('/media', { method: 'POST', body: JSON.stringify(data) }),

  updateMedia: (id, data) =>
    request(`/media/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteMedia: (id) =>
    request(`/media/${id}`, { method: 'DELETE' }),

  // Stubs
  people:    () => Promise.resolve([]),
  practices: () => Promise.resolve([]),
};

export const publicInsights = createInsightsCache(options => api.insights(options));

export default api;
